import React, { useCallback, useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { adminAPI } from '../../../api';
import useAlarm from '../../../context/AlarmContext';
import FormInput from '../../../components/FormInput';
import SelectInput from '../../../components/SelectInput';
import SearchableSelectInput from '../../../components/SearchableSelectInput';
import Button from '../../../components/Button';

const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  } catch (error) {
    return '';
  }
};

const EditCohortForm = ({ cohort, onEdited }) => {
  const { setAlarm } = useAlarm();
  const [formData, setFormData] = useState({
    name: '',
    alex_about_cohort: '',
    user_access_type: '',
    alex_access_type: '',
    company_name: '',
    company_id: '',
    start_date: '',
    user_expiration_date: '',
    alex_expiration_date: '',
    status: '',
    splash_screen: '',
    cohort_pricing: '',
    cra_contact: '',
    account_manager: '',
    primary_client_contact_name: '',
    primary_client_contact_email: '',
    total_seats: '',
    used_seats: '',
    available_seats: '',
    cohort_notes: '',
    reporting_cadence: '',
    cohort_courses: [],
  });

  const { data: invitationData } = useQuery(
    'getInvitationData',
    adminAPI.getInvitationData
  );

  const courses = cohort?.courses || [];
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [isCheckAll, setIsCheckAll] = useState(false);

  useEffect(() => {
    if (cohort) {
      setFormData({
        name: cohort.name || '',
        alex_about_cohort: cohort.alex_about_cohort || '',
        user_access_type: cohort.user_access_type || '',
        alex_access_type: cohort.alex_access_type || '',
        company_name: cohort.company_name || '',
        company_id: cohort.company_id || '',
        start_date: formatDateForInput(cohort.start_date),
        user_expiration_date: formatDateForInput(cohort.user_expiration_date),
        alex_expiration_date: formatDateForInput(cohort.alex_expiration_date),
        status: cohort.status || '',
        splash_screen: cohort.splash_screen || '',
        cohort_pricing: cohort.cohort_pricing || '',
        cra_contact: cohort.cra_contact || '',
        account_manager: cohort.account_manager || '',
        primary_client_contact_name: cohort.primary_client_contact_name || '',
        primary_client_contact_email: cohort.primary_client_contact_email || '',
        total_seats: cohort.total_seats || '',
        used_seats: cohort.used_seats || '',
        available_seats: cohort.available_seats || '',
        cohort_notes: cohort.cohort_notes || '',
        reporting_cadence: cohort.reporting_cadence || '',
        cohort_courses: [],
      });
    }
  }, [cohort]);

  useEffect(() => {
    if (cohort?.cohort_courses && cohort.cohort_courses.length > 0) {
      const initiallyEnrolledCourses = cohort.cohort_courses
        .map((course) => course?.id);
      setSelectedCourses(initiallyEnrolledCourses);
    }
  }, [cohort?.cohort_courses]);

  const handleCheckAll = (checked) => {
    if (checked) {
      setSelectedCourses(cohort.courses.map((course) => course?.id));
      setIsCheckAll(true);
    } else {
      setSelectedCourses([]);
      setIsCheckAll(false);
    }
  };

  const handleCourseCheck = (checked, courseId) => {
    if (checked) {
      setSelectedCourses((prevCourses) => [...prevCourses, courseId]);
    } else {
      setSelectedCourses((prevCourses) =>
        prevCourses.filter((id) => id !== courseId)
      );
    }
  };

  useEffect(() => {
    if (cohort?.courses) {
      setIsCheckAll(selectedCourses.length === cohort.courses.length);
    }
  }, [selectedCourses, cohort?.courses]);

  const { mutate: updateCohortMutation, isLoading } = useMutation(
    adminAPI.updateCohort,
    {
      onSuccess: () => {
        setAlarm({ type: 'success', message: 'Cohort updated successfully' });
        onEdited();
      },
      onError: (error) => {
        const errors = error?.response?.data?.errors;
        const errorMessage = errors?.length > 0 
          ? errors.join(', ')
          : error.message;
        setAlarm({ type: 'error', message: errorMessage });
      },
    }
  );

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const data = {
        id: cohort.id,
        cohort: {
          name: formData.name,
          alex_about_cohort: formData.alex_about_cohort,
          user_access_type: formData.user_access_type,
          alex_access_type: formData.alex_access_type,
          company_name: formData.company_name,
          company_id: formData.company_id,
          start_date: formData.start_date,
          user_expiration_date: formData.user_expiration_date,
          alex_expiration_date: formData.alex_expiration_date,
          status: formData.status,
          splash_screen: formData.splash_screen,
          cohort_pricing: formData.cohort_pricing,
          cra_contact: formData.cra_contact,
          account_manager: formData.account_manager,
          primary_client_contact_name: formData.primary_client_contact_name,
          primary_client_contact_email: formData.primary_client_contact_email,
          total_seats: formData.total_seats,
          used_seats: formData.used_seats,
          available_seats: formData.available_seats,
          cohort_notes: formData.cohort_notes,
          reporting_cadence: formData.reporting_cadence,
          cohort_courses: selectedCourses.map((courseId) => ({ id: courseId })),
        },
      };
      updateCohortMutation(data);
    },
    [cohort, formData, selectedCourses, updateCohortMutation]
  );

  if (!cohort) return <div>Loading...</div>;

  const hubspotAccessTypes = [
    { value: '', label: '' },
    ...invitationData?.hubspot_options || []
  ];

  const alexAccessTypes = [
    { value: '', label: '' },
    ...invitationData?.alex_access_type_options || []
  ];
  
  const statusOptions = [
    { value: '', label: '' },
    { value: 'Active', label: 'Active' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Unconfirmed', label: 'Unconfirmed' },
    { value: 'Upcoming', label: 'Upcoming' },
  ];

  const splashScreenOptions = invitationData?.splash_screen_options || [{ value: '', label: 'None' }];

  return (
    <form onSubmit={handleSubmit}>
      <div className="pb-4" style={{ width: '400px' }}>
        <FormInput
          label="Name *"
          name="name"
          value={formData.name}
          type="text"
          handleChange={(e) => handleChange('name', e.target.value)}
          required
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <label className="text-sm font-bold text-gray-700 block pb-2">
          About my organization
        </label>
        <textarea
          name="alex_about_cohort"
          value={formData.alex_about_cohort}
          onChange={(e) => handleChange('alex_about_cohort', e.target.value)}
          className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600"
          rows="4"
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <p className="text-sm text-charcoal font-bold pb-2">User Access Type</p>
        <SelectInput
          name="user_access_type"
          value={formData.user_access_type}
          options={hubspotAccessTypes}
          onChange={handleChange}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <p className="text-sm text-charcoal font-bold pb-2">Alex Access Type</p>
        <SelectInput
          name="alex_access_type"
          value={formData.alex_access_type}
          options={alexAccessTypes}
          onChange={handleChange}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <p className="text-sm text-charcoal font-bold pb-2">Company</p>
        <SearchableSelectInput
          name="company_id"
          value={formData.company_id}
          options={[
            { value: '', label: 'None' },
            ...(invitationData?.company_options || [])
          ]}
          onChange={(name, value) => {
            const selected = (invitationData?.company_options || []).find((o) => String(o.value) === String(value));
            setFormData((prev) => ({
              ...prev,
              company_id: value,
              company_name: selected ? selected.label : '',
            }));
          }}
          placeholder="Search companies..."
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <FormInput
          label="Start Date"
          name="start_date"
          value={formData.start_date}
          type="date"
          handleChange={(e) => handleChange('start_date', e.target.value)}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <FormInput
          label="User Expiration Date"
          name="user_expiration_date"
          value={formData.user_expiration_date}
          type="date"
          handleChange={(e) => handleChange('user_expiration_date', e.target.value)}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <FormInput
          label="Alex Expiration Date"
          name="alex_expiration_date"
          value={formData.alex_expiration_date}
          type="date"
          handleChange={(e) => handleChange('alex_expiration_date', e.target.value)}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <p className="text-sm text-charcoal font-bold pb-2">Splash Screen</p>
        <SelectInput
          name="splash_screen"
          value={formData.splash_screen}
          options={splashScreenOptions}
          onChange={handleChange}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <p className="text-sm text-charcoal font-bold pb-2">Status</p>
        <SelectInput
          name="status"
          value={formData.status}
          options={statusOptions}
          onChange={handleChange}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <FormInput
          label="Cohort Pricing"
          name="cohort_pricing"
          value={formData.cohort_pricing}
          type="text"
          handleChange={(e) => handleChange('cohort_pricing', e.target.value)}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <FormInput
          label="CRA Contact"
          name="cra_contact"
          value={formData.cra_contact}
          type="text"
          handleChange={(e) => handleChange('cra_contact', e.target.value)}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <FormInput
          label="Account Manager"
          name="account_manager"
          value={formData.account_manager}
          type="text"
          handleChange={(e) => handleChange('account_manager', e.target.value)}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <FormInput
          label="Primary Client Contact Name"
          name="primary_client_contact_name"
          value={formData.primary_client_contact_name}
          type="text"
          handleChange={(e) => handleChange('primary_client_contact_name', e.target.value)}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <FormInput
          label="Primary Client Contact Email"
          name="primary_client_contact_email"
          value={formData.primary_client_contact_email}
          type="text"
          handleChange={(e) => handleChange('primary_client_contact_email', e.target.value)}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <FormInput
          label="Total Seats"
          name="total_seats"
          value={formData.total_seats}
          type="number"
          handleChange={(e) => handleChange('total_seats', e.target.value)}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <FormInput
          label="Used Seats"
          name="used_seats"
          value={formData.used_seats}
          type="number"
          handleChange={(e) => handleChange('used_seats', e.target.value)}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <FormInput
          label="Available Seats"
          name="available_seats"
          value={formData.available_seats}
          type="number"
          handleChange={(e) => handleChange('available_seats', e.target.value)}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <p className="text-sm text-charcoal font-bold pb-2">Reporting Cadence</p>
        <SelectInput
          name="reporting_cadence"
          value={formData.reporting_cadence}
          options={[
            { value: '', label: '' },
            { value: 'none', label: 'None' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'quarterly', label: 'Quarterly' }
          ]}
          onChange={handleChange}
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <label className="text-sm font-bold text-gray-700 block pb-2">Cohort Notes</label>
        <textarea
          name="cohort_notes"
          value={formData.cohort_notes}
          onChange={(e) => handleChange('cohort_notes', e.target.value)}
          className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600"
          rows="4"
        />
      </div>
      <div className="pb-4" style={{ width: '400px' }}>
        <p className="text-sm uppercase text-charcoal font-bold pb-2">Modules</p>
        <div className="rounded-lg border-2 border-gray p-2 relative overflow-hidden">
          <div className="p-4 flex items-center border-b-2 border-gray">
            <input
              id="select-all"
              className="mr-4"
              type="checkbox"
              checked={isCheckAll}
              onChange={(e) => handleCheckAll(e.target.checked)}
            />
            <label htmlFor="select-all">Select All</label>
          </div>
          {courses && courses.length > 0 ? courses.map((course) => {
            const { id, title } = course;
            return (
              <div className="px-4 py-2 flex items-center" key={id}>
                <input
                  id={id}
                  className="mr-4"
                  type="checkbox"
                  checked={selectedCourses.includes(id)}
                  onChange={(e) =>
                    handleCourseCheck(e.target.checked, id)
                  }
                />
                <label htmlFor={id}>{title}</label>
              </div>
            );
          }) : <div className="p-4 text-gray-500">No courses available</div>}
        </div>
      </div>
      <Button
        type="submit"
        variant="default-lowercase"
        className="font-bold capitalize"
        style={{ borderRadius: '12px' }}
        disabled={isLoading}
      >
        {isLoading ? 'Updating...' : 'Update Cohort'}
      </Button>
    </form>
  );
};

export default EditCohortForm;
